import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Users, Plus, Edit2, Trash2, Link2, Eye, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const typeLabels = { general: 'General', influencer: 'Influencer', vendedor: 'Vendedor' };

export const EngancheTab = ({ token }) => {
  const [referrers, setReferrers] = useState([]);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [showAssign, setShowAssign] = useState(false);
  const [selectedReferrer, setSelectedReferrer] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [form, setForm] = useState({
    email: '', password: '', name: '', phone: '', referrer_type: 'general', commission_percentage: 10
  });
  const [assignForm, setAssignForm] = useState({ referrer_id: '', provider_id: '' });

  const headers = { Authorization: `Bearer ${token}` };

  const fetchReferrers = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/referrers/admin/list`, { headers });
      setReferrers(res.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, [token]);

  const fetchProviders = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/providers`, { headers });
      setProviders(res.data || []);
    } catch (e) { console.error(e); }
  }, [token]);

  useEffect(() => { fetchReferrers(); fetchProviders(); }, [fetchReferrers, fetchProviders]);

  const handleCreate = async () => {
    try {
      await axios.post(`${API_URL}/api/referrers/admin/create`, form, { headers });
      toast.success('Enganche creado exitosamente');
      setShowCreate(false);
      setForm({ email: '', password: '', name: '', phone: '', referrer_type: 'general', commission_percentage: 10 });
      fetchReferrers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al crear enganche');
    }
  };

  const handleUpdate = async () => {
    try {
      await axios.put(`${API_URL}/api/referrers/admin/${selectedReferrer.id}`, {
        name: form.name, phone: form.phone,
        referrer_type: form.referrer_type, commission_percentage: form.commission_percentage,
        is_active: form.is_active
      }, { headers });
      toast.success('Enganche actualizado');
      setShowEdit(false);
      fetchReferrers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al actualizar');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminar este enganche?')) return;
    try {
      await axios.delete(`${API_URL}/api/referrers/admin/${id}`, { headers });
      toast.success('Enganche eliminado');
      fetchReferrers();
    } catch (e) { toast.error('Error al eliminar'); }
  };

  const handleAssign = async () => {
    try {
      await axios.post(`${API_URL}/api/referrers/admin/assign`, assignForm, { headers });
      toast.success('Enganche asignado al proveedor');
      setShowAssign(false);
      fetchReferrers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al asignar');
    }
  };

  const openDetail = async (referrer) => {
    try {
      const res = await axios.get(`${API_URL}/api/referrers/admin/${referrer.id}/detail`, { headers });
      setDetailData(res.data);
      setShowDetail(true);
    } catch (e) { toast.error('Error al cargar detalle'); }
  };

  const openEdit = (r) => {
    setSelectedReferrer(r);
    setForm({ name: r.name, phone: r.phone || '', referrer_type: r.referrer_type, commission_percentage: r.commission_percentage, is_active: r.is_active });
    setShowEdit(true);
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00CC66]" /></div>;

  return (
    <div className="space-y-6" data-testid="enganche-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-[#064E3B]">Enganches</h2>
          <p className="text-sm text-gray-500">{referrers.length} enganches registrados</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="rounded-xl" onClick={() => setShowAssign(true)} data-testid="assign-enganche-btn">
            <UserPlus className="w-4 h-4 mr-2" /> Asignar
          </Button>
          <Button className="bg-[#00CC66] hover:bg-[#00b359] rounded-xl" onClick={() => setShowCreate(true)} data-testid="create-enganche-btn">
            <Plus className="w-4 h-4 mr-2" /> Nuevo Enganche
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-xl font-bold">{referrers.length}</p>
              <p className="text-xs text-gray-500">Total enganches</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-green-50 flex items-center justify-center">
              <Link2 className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-xl font-bold">{referrers.reduce((sum, r) => sum + r.referral_count, 0)}</p>
              <p className="text-xs text-gray-500">Total referidos</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-yellow-50 flex items-center justify-center">
              <span className="text-yellow-600 font-bold text-sm">$</span>
            </div>
            <div>
              <p className="text-xl font-bold">${referrers.reduce((sum, r) => sum + r.total_earned, 0).toLocaleString('es-CL')}</p>
              <p className="text-xs text-gray-500">Total comisiones</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="enganche-table">
              <thead>
                <tr className="border-b bg-gray-50 text-left text-gray-500">
                  <th className="px-4 py-3 font-medium">Nombre</th>
                  <th className="px-4 py-3 font-medium">Tipo</th>
                  <th className="px-4 py-3 font-medium">Comision</th>
                  <th className="px-4 py-3 font-medium">Codigo</th>
                  <th className="px-4 py-3 font-medium">Referidos</th>
                  <th className="px-4 py-3 font-medium">Ganado</th>
                  <th className="px-4 py-3 font-medium">Estado</th>
                  <th className="px-4 py-3 font-medium">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {referrers.map((r) => (
                  <tr key={r.id} className="border-b last:border-0 hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-[#064E3B]">{r.name}</p>
                        <p className="text-xs text-gray-400">{r.email}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant="outline">{typeLabels[r.referrer_type] || r.referrer_type}</Badge>
                    </td>
                    <td className="px-4 py-3 font-semibold">{r.commission_percentage}%</td>
                    <td className="px-4 py-3 font-mono text-xs">{r.referral_code}</td>
                    <td className="px-4 py-3 text-center">{r.referral_count}</td>
                    <td className="px-4 py-3">${r.total_earned.toLocaleString('es-CL')}</td>
                    <td className="px-4 py-3">
                      <Badge className={r.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                        {r.is_active ? 'Activo' : 'Inactivo'}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openDetail(r)} data-testid={`view-enganche-${r.id}`}><Eye className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(r)} data-testid={`edit-enganche-${r.id}`}><Edit2 className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => handleDelete(r.id)} data-testid={`delete-enganche-${r.id}`}><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle>Nuevo Enganche</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nombre *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="enganche-name-input" /></div>
            <div><Label>Email *</Label><Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} data-testid="enganche-email-input" /></div>
            <div><Label>Contrasena *</Label><Input type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} data-testid="enganche-password-input" /></div>
            <div><Label>Telefono</Label><Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} /></div>
            <div>
              <Label>Tipo</Label>
              <Select value={form.referrer_type} onValueChange={v => setForm({...form, referrer_type: v})}>
                <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">General</SelectItem>
                  <SelectItem value="influencer">Influencer</SelectItem>
                  <SelectItem value="vendedor">Vendedor</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Comision (%)</Label><Input type="number" min="0" max="100" value={form.commission_percentage} onChange={e => setForm({...form, commission_percentage: parseFloat(e.target.value) || 0})} data-testid="enganche-commission-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancelar</Button>
            <Button className="bg-[#00CC66]" onClick={handleCreate} data-testid="save-enganche-btn">Crear Enganche</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle>Editar Enganche</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nombre</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} /></div>
            <div><Label>Telefono</Label><Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} /></div>
            <div>
              <Label>Tipo</Label>
              <Select value={form.referrer_type} onValueChange={v => setForm({...form, referrer_type: v})}>
                <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">General</SelectItem>
                  <SelectItem value="influencer">Influencer</SelectItem>
                  <SelectItem value="vendedor">Vendedor</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Comision (%)</Label><Input type="number" min="0" max="100" value={form.commission_percentage} onChange={e => setForm({...form, commission_percentage: parseFloat(e.target.value) || 0})} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEdit(false)}>Cancelar</Button>
            <Button className="bg-[#00CC66]" onClick={handleUpdate}>Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Dialog */}
      <Dialog open={showAssign} onOpenChange={setShowAssign}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle>Asignar Enganche a Proveedor</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Enganche</Label>
              <Select value={assignForm.referrer_id} onValueChange={v => setAssignForm({...assignForm, referrer_id: v})}>
                <SelectTrigger className="rounded-xl"><SelectValue placeholder="Selecciona enganche" /></SelectTrigger>
                <SelectContent>
                  {referrers.filter(r => r.is_active).map(r => (
                    <SelectItem key={r.id} value={r.id}>{r.name} ({r.referral_code})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Proveedor</Label>
              <Select value={assignForm.provider_id} onValueChange={v => setAssignForm({...assignForm, provider_id: v})}>
                <SelectTrigger className="rounded-xl"><SelectValue placeholder="Selecciona proveedor" /></SelectTrigger>
                <SelectContent>
                  {providers.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.business_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssign(false)}>Cancelar</Button>
            <Button className="bg-[#00CC66]" onClick={handleAssign}>Asignar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="rounded-2xl max-w-2xl max-h-[80vh] overflow-y-auto">
          {detailData && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {detailData.referrer.name}
                  <Badge variant="outline">{typeLabels[detailData.referrer.referrer_type]}</Badge>
                  <Badge className="bg-[#064E3B] text-white">{detailData.referrer.commission_percentage}%</Badge>
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="flex items-center gap-4 text-sm text-gray-600">
                  <span>Codigo: <strong className="font-mono">{detailData.referrer.referral_code}</strong></span>
                  <span>Email: {detailData.referrer.email}</span>
                </div>
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div className="bg-blue-50 rounded-xl p-3"><p className="text-lg font-bold text-blue-700">{detailData.summary.total_referrals}</p><p className="text-xs text-blue-600">Referidos</p></div>
                  <div className="bg-green-50 rounded-xl p-3"><p className="text-lg font-bold text-green-700">${detailData.summary.total_earned.toLocaleString('es-CL')}</p><p className="text-xs text-green-600">Ganado</p></div>
                  <div className="bg-yellow-50 rounded-xl p-3"><p className="text-lg font-bold text-yellow-700">${detailData.summary.total_pending.toLocaleString('es-CL')}</p><p className="text-xs text-yellow-600">Pendiente</p></div>
                </div>
                <div>
                  <h4 className="font-semibold text-sm mb-2 text-[#064E3B]">Proveedores Referidos</h4>
                  {detailData.referrals.length === 0 ? (
                    <p className="text-sm text-gray-400">Sin referidos aun</p>
                  ) : (
                    <table className="w-full text-sm">
                      <thead><tr className="border-b text-gray-500"><th className="pb-2 text-left">Proveedor</th><th className="pb-2 text-left">Plan</th><th className="pb-2 text-left">Precio</th><th className="pb-2 text-left">Meses gratis</th></tr></thead>
                      <tbody>
                        {detailData.referrals.map(r => (
                          <tr key={r.id} className="border-b last:border-0">
                            <td className="py-2">{r.provider_name}</td>
                            <td className="py-2"><Badge variant="outline">{r.plan_name}</Badge></td>
                            <td className="py-2">${r.plan_price?.toLocaleString('es-CL')}</td>
                            <td className="py-2">{r.free_months_remaining > 0 ? <Badge className="bg-yellow-100 text-yellow-700">{r.free_months_remaining}</Badge> : <Badge className="bg-green-100 text-green-700">Activo</Badge>}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
                {detailData.commissions.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-sm mb-2 text-[#064E3B]">Comisiones</h4>
                    <table className="w-full text-sm">
                      <thead><tr className="border-b text-gray-500"><th className="pb-2 text-left">Periodo</th><th className="pb-2 text-left">Monto</th><th className="pb-2 text-left">Estado</th></tr></thead>
                      <tbody>
                        {detailData.commissions.map((c, i) => (
                          <tr key={i} className="border-b last:border-0">
                            <td className="py-2">{c.month}/{c.year}</td>
                            <td className="py-2 font-semibold">${c.amount.toLocaleString('es-CL')}</td>
                            <td className="py-2"><Badge className={c.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>{c.status === 'paid' ? 'Pagado' : 'Pendiente'}</Badge></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
